import { agendaNoteText } from '@/lib/agendaPresentation';
import {NativeAppleCalendarActionModal} from '@/components/mirror/NativeAppleCalendarActionModal';
import {AppleCalendarPendingActionLauncher} from '@/components/mirror/AppleCalendarPendingActionLauncher';
import {confirmAppleMirrorReceipt} from '@/lib/appleMirrorReceipt';
import MirrorBackButton from "@/components/MirrorBackButton";
import { snapshotForTask } from "@/lib/taskMutations";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence, MotionConfig } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import AgendaDayPlan from "@/components/agenda/AgendaDayPlan";
import { occursDuringDay } from "@/lib/agendaDayPlan";
import {
  CalendarDays,
  Plus,
  ChevronLeft,
  StickyNote,
  ChevronRight,
  ArrowRight,
  Clock,
  Bell,
  Trash2,
  Pencil,
  Sparkles,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { calendarInstant } from "@/lib/calendarAction";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageProvider";
import { localeTagFor, type Lang } from "@/i18n/locale";
import { useXP } from "@/hooks/useXP";
import {
  useAgendaVoiceCapture,
  type ExtractedAgendaEvent,
} from "@/hooks/useAgendaVoiceCapture";
import { Mic } from "lucide-react";
import {
  AgendaMirrorProvider,
  AgendaMiniMirrorAvatarSlot,
  AgendaMiniMirrorScorecard,
  AgendaMiniMirrorVoiceSlot,
} from "@/components/agenda/AgendaMiniMirror";
import { useAgendaSnapshot } from "@/hooks/useAgendaSnapshot";
import { useLocalAgendaResource } from "@/hooks/useLocalAgendaResource";
import { useLibraryIdentity } from "@/hooks/useLibraryIdentity";
import { LocalAgendaActionModal, AgendaPendingActionLauncher } from "@/components/mirror/LocalAgendaActionModal";
import { newAgendaOperationId, agendaOwnerSession, snapshotForAgenda } from "@/lib/agendaExecutor";
import { takeOwnedAgendaContext } from "@/lib/agendaContextHandoff";
import { isAllDay, eventStartDate, eventOccursOn, formatAllDayDates } from "@/lib/agendaDates";
import { AgendaSessionBoundary } from "@/components/agenda/AgendaSessionBoundary";
import AgendaTasksCard from "@/components/agenda/AgendaTasksCard";
import { useAgendaTasks, type AgendaTask } from "@/hooks/useAgendaTasks";
// Modal + shared agenda primitives — extracted 06/05 so MirrorChat can
// reuse the same modal for Mirror-driven event creation.
import {
  CreateEventModal,
  AgendaBottomSheet,
  type EventTag,
  type CreateEventPrefill,
  TAG_STYLES,
  TAG_OPTIONS,
  TAG_KEYS,
  REMINDER_OPTIONS,
  DAY_KEYS,
  getWeekDays,
  isSameDay,
  formatTime,
  toLocalDatetimeString,
} from "@/components/agenda/CreateEventModal";
import RemindersEditor from "@/components/agenda/RemindersEditor";
import {
  loadEventReminders,
  replaceEventReminders,
} from "@/lib/agendaReminders";

// ── Types ──────────────────────────────────────────────────────────────────

export interface AgendaEvent {
  all_day_start?: string | null;
  all_day_end?: string | null;
  id: string;
  title: string;
  tag: EventTag;
  start_at: string;
  end_at: string | null;
  notes: string | null;
  source: string;
  reminder_minutes: number | null;
}

// ── Helpers (page-local only) ──────────────────────────────────────────────

function formatUpcomingLabel(d: Date): string {
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

function startOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

function endOfDay(d: Date): Date {
  const r = new Date(d);
  r.setHours(23, 59, 59, 999);
  return r;
}

// AgendaBottomSheet, REMINDER_OPTIONS, REMINDER_BADGE_KEYS imported from
// @/components/agenda/CreateEventModal (extracted 06/05).

// ── EventCard ──────────────────────────────────────────────────────────────

const EventCard: React.FC<{
  event: AgendaEvent;
  reminderCount?: number;
  onTap?: (event: AgendaEvent) => void;
}> = ({ event, reminderCount, onTap }) => {
  const navigate = useNavigate();
  const { t, tf, lang } = useLanguage();
  // Defensive lookup — event.tag comes from the DB and any future
  // unknown value (e.g., the brief 'external' tag bug fixed 19/05 in
  // google-cal-pull) must NOT crash EventCard. Falls back to the
  // Personal style + label as the neutral default.
  const tagStyle = TAG_STYLES[event.tag] ?? TAG_STYLES.Personal;

  const handlePrepare = (e: React.MouseEvent) => {
    e.stopPropagation();
    const time = isAllDay(event) ? t("ux.agenda.allDay") : formatTime(event.start_at);
    navigate("/mirror", {
      state: {
        source: "agenda",
        eventId: event.id,
        eventTitle: event.title,
        eventTag: event.tag,
        eventStartAt: event.start_at,
        prompt: `Help me prepare for "${event.title}" at ${time}. This is a ${(event.tag ?? "Personal").toLowerCase()} event.`,
      },
    });
  };

  // Reminder label: render via a generic {n} min antes / before / van tevoren
  // template so arbitrary values (e.g. the new Personal default 10) work
  // without per-value badge keys.
  const reminder =
    event.reminder_minutes !== null
      ? tf("agenda.event.reminderMinutesBefore", { n: String(event.reminder_minutes) })
      : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-4 mb-3 relative overflow-hidden cursor-pointer active:scale-[0.98] transition-transform duration-150"
      style={{
        background: "linear-gradient(135deg, hsl(220 40% 9%), hsl(220 35% 6%))",
        border: "1px solid hsl(187 72% 50% / 0.22)",
        borderLeft: "3px solid hsl(187 72% 55%)",
        boxShadow: "0 0 20px hsl(187 72% 50% / 0.08), inset 3px 0 16px -6px hsl(187 72% 50% / 0.15)",
      }}
      onClick={() => onTap?.(event)}
    >
      {/* Star field */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
        {Array.from({ length: 6 }, (_, si) => (
          <motion.div key={`ev-${event.id}-${si}`} className="absolute rounded-full"
            style={{ width: si % 2 === 0 ? 1.5 : 0.8, height: si % 2 === 0 ? 1.5 : 0.8, background: si % 2 === 0 ? "#00e5ff" : "hsl(265 60% 75%)", opacity: 0.12, left: `${(si * 17) % 90}%`, top: `${(si * 23) % 80}%` }}
            animate={{ y: [0, -(6 + si * 3), 0], opacity: [0.05, 0.18, 0.05] }}
            transition={{ duration: 4 + si, repeat: Infinity, ease: "easeInOut", delay: si * 0.6 }} />
        ))}
      </div>
      <div className="flex items-start justify-between mb-2 relative z-10">
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] font-display tracking-wide font-medium"
            style={{ color: "hsl(187 72% 50% / 0.55)" }}
          >
            {isAllDay(event) ? t("ux.agenda.allDay") : formatTime(event.start_at)}
            {!isAllDay(event) && event.end_at ? ` – ${formatTime(event.end_at)}` : ""}
          </span>
          {reminder && (
            <span
              className="flex items-center gap-0.5 text-[8px] font-display tracking-wide px-1.5 py-0.5 rounded-full"
              style={{
                background: "hsl(187 72% 50% / 0.08)",
                color: "hsl(187 72% 50% / 0.50)",
              }}
            >
              <Bell size={7} />
              {reminder}
            </span>
          )}
          {/* 🔔 badge — surfaces when event_reminders has rows for this
              event. Renders even when the legacy reminder_minutes column
              is null (multi-reminder events created via the new modal). */}
          {reminderCount !== undefined && reminderCount > 0 && (
            <span
              aria-label={t("reminders.badge.ariaLabel")}
              title={t("reminders.badge.ariaLabel")}
              className="inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[8px] font-display font-bold"
              style={{
                background: "hsl(187 72% 50% / 0.15)",
                color: "hsl(187 72% 60%)",
                border: "1px solid hsl(187 72% 50% / 0.30)",
                lineHeight: 1,
              }}
            >
              🔔{reminderCount > 1 ? " " + reminderCount : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Pencil size={10} style={{ color: "hsl(218 14% 45%)" }} />
          <span
            className="text-[9px] font-display tracking-[0.14em] uppercase px-2 py-0.5 rounded-full"
            style={{ background: tagStyle.bg, color: tagStyle.text, border: `1px solid ${tagStyle.text}40`, boxShadow: `0 0 8px ${tagStyle.bg}` }}
          >
            {t(TAG_KEYS[event.tag] ?? TAG_KEYS.Personal)}
          </span>
        </div>
      </div>
      <p className="font-display text-[14px] font-semibold text-foreground leading-snug mb-1 relative z-10">
        {event.title}
      </p>
      {isAllDay(event) && <p className="mb-2 text-sm text-white/65">{formatAllDayDates(event, localeTagFor(lang))}</p>}
      {event.notes && (
        <p
          className="text-[11px] font-display leading-relaxed mb-2 whitespace-pre-wrap break-words [overflow-wrap:anywhere]"
          style={{ color: "hsl(218 14% 55%)" }}
        >
          {agendaNoteText(event.notes, event.source)}
        </p>
      )}
      <button
        onClick={handlePrepare}
        className="flex items-center gap-1 text-[10px] font-display tracking-wide transition-opacity hover:opacity-100 opacity-70 mt-1"
        style={{ color: "hsl(187 72% 50%)" }}
      >
        <span>{t("agenda.prepareWithMirror")}</span>
        <ArrowRight size={10} />
      </button>
    </motion.div>
  );
};

// ── EditEventModal ────────────────────────────────────────────────────────

interface EditModalProps {
  event: AgendaEvent | null;
  onClose: () => void;
  onUpdated: () => void;
}

export const EditEventModal: React.FC<EditModalProps> = (props) => {
  if (!props.event) return null;
  if(['apple','apple_calendar'].includes(props.event.source)||(props.event as {external_provider?:string|null}).external_provider==='apple')return <AppleImportedEventEditor {...props}/>;
  const imported = ["google", "google_calendar", "outlook", "apple", "apple_calendar"].includes(props.event.source) || !!(props.event as { external_provider?: string | null }).external_provider;
  return imported ? <ImportedEventEditModal {...props} /> : <LocalEventEditor key={props.event.id} {...props} />;
};
function AppleImportedEventEditor({event,onClose,onUpdated}:EditModalProps){
 const identity=useLibraryIdentity(),{lang}=useLanguage();
 return <NativeAppleCalendarActionModal open={!!event} prefill={event?{provider:'apple_calendar',action:'search',query:event.title,action_id:'standalone:'+event.id,expected_user_id:identity.userId??undefined}:null}
  onClose={onClose} onConfirmed={onUpdated} onCreated={async receipt=>!receipt||!!await confirmAppleMirrorReceipt({client:supabase,receipt,language:lang,isCurrent:()=>identity.userId===receipt.userId})}/>;
}
function LocalEventEditor({ event, onClose, onUpdated }: EditModalProps) {
  const { t } = useLanguage();
  const [operationId] = useState(newAgendaOperationId);
  const [originalSnapshot] = useState(() => snapshotForAgenda(event));
  const refreshReview = () => { onUpdated(); onClose(); };
  if (!originalSnapshot) return <AgendaBottomSheet open onClose={onClose} closeLabel={t("common.close")} title={t("agendaAction.title")} footer={<button onClick={refreshReview}>{t("agendaAction.refreshReview")}</button>}><p role="alert">{t("agendaAction.readOnly")}</p></AgendaBottomSheet>;
  return <LocalAgendaActionModal open={!!event} prefill={event ? { action: "update", event_id: event.id, expected_snapshot: originalSnapshot, operation_id: operationId, action_id: "standalone:" + operationId } : null} onClose={onClose} onCreated={() => undefined} onConfirmed={() => onUpdated()} onRefreshReview={refreshReview} />;
}
const ImportedEventEditModal: React.FC<EditModalProps> = ({ event, onClose, onUpdated }) => {
  const { t, lang } = useLanguage();
  const open = event !== null;

  const [title, setTitle] = useState("");
  const [tag, setTag] = useState<EventTag>("Focus");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [notes, setNotes] = useState("");
  const [reminderMinutes, setReminderMinutes] = useState<number | null>(null);
  const [reminders, setReminders] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (event) {
      setTitle(event.title);
      setTag(event.tag);
      setStartAt(toLocalDatetimeString(new Date(event.start_at)));
      setEndAt(event.end_at ? toLocalDatetimeString(new Date(event.end_at)) : "");
      setNotes(event.notes ?? "");
      setReminderMinutes(event.reminder_minutes);
      setConfirmDelete(false);
      // Hydrate the multi-reminder list from event_reminders. Falls back
      // to the legacy single agenda_events.reminder_minutes when no rows
      // exist yet (events created before the migration land here).
      let cancelled = false;
      void (async () => {
        const remote = await loadEventReminders(event.id);
        if (cancelled) return;
        if (remote.length > 0) {
          setReminders(remote);
        } else if (event.reminder_minutes !== null && event.reminder_minutes !== undefined) {
          setReminders([event.reminder_minutes]);
        } else {
          setReminders([]);
        }
      })();
      return () => { cancelled = true; };
    }
  }, [event]);

  const handleUpdate = async () => {
    if (!event) return;
    if (!title.trim()) { toast.error(t("agenda.titleRequired")); return; }
    if (!startAt) { toast.error(t("agenda.startRequired")); return; }
    const timedDates = isAllDay(event) ? null : {
      start_at: calendarInstant(startAt, event.start_at),
      end_at: endAt ? calendarInstant(endAt, event.end_at ?? undefined) : null,
    };
    const changedTimes = timedDates && (timedDates.start_at !== event.start_at || timedDates.end_at !== event.end_at);
    if (timedDates && (!timedDates.start_at || (endAt && !timedDates.end_at) || (changedTimes && timedDates.end_at && Date.parse(timedDates.start_at) >= Date.parse(timedDates.end_at)))) {
      toast.error(t("mirror.calendar.invalidDates")); return;
    }
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("agenda_events")
        .update({
          title: title.trim(),
          tag,
          ...(timedDates ?? {}),
          notes: notes.trim() || null,
          // Keep the legacy single column in sync with reminders[0]. Same
          // contract as CreateEventModal so any consumer that still reads
          // agenda_events.reminder_minutes sees the same value.
          reminder_minutes: reminders.length > 0 ? reminders[0] : null,
        })
        .eq("id", event.id);
      if (error) throw error;
      if (user) {
        // Best-effort: a reminder write failure shouldn't undo the event
        // edit. Errors are logged inside replaceEventReminders.
        await replaceEventReminders({
          eventId: event.id,
          userId: user.id,
          reminders,
        });
      }
      toast.success(t("agenda.eventUpdated"));
      onUpdated();
      onClose();
    } catch (cause: unknown) {
      toast.error(cause && typeof cause === "object" && "message" in cause && typeof cause.message === "string" ? cause.message : t("agenda.failedToUpdate"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!event) return;
    setDeleting(true);
    try {
      // Provider-synced events (google/outlook/apple) soft-delete: the row
      // must stay as a tombstone so the provider sync can RESPECT the user's
      // deletion instead of re-inserting the event on the next pull (a hard
      // delete leaves nothing to diff against — the event resurrected).
      // Manual events keep the hard delete (no sync can resurrect them).
      const isProviderEvent = Boolean((event as { external_provider?: string | null }).external_provider);
      const { error } = isProviderEvent
        ? await supabase
            .from("agenda_events")
            .update({ deleted_at: new Date().toISOString() } as never)
            .eq("id", event.id)
        : await supabase
            .from("agenda_events")
            .delete()
            .eq("id", event.id);
      if (error) throw error;
      toast.success(t("agenda.eventDeleted"));
      onUpdated();
      onClose();
    } catch (cause: unknown) {
      toast.error(cause && typeof cause === "object" && "message" in cause && typeof cause.message === "string" ? cause.message : t("agenda.failedToDelete"));
    } finally {
      setDeleting(false);
    }
  };

  const inputStyle = {
    background: "hsl(var(--card))",
    border: "1px solid hsl(187 72% 50% / 0.15)",
  };
  const labelStyle = { color: "hsl(187 72% 50% / 0.55)" };

  const editFooter = (
    <button
      onClick={handleUpdate}
      disabled={saving}
      className="w-full py-3.5 rounded-xl font-display text-[14px] font-semibold tracking-wide transition-all duration-200 active:scale-[0.98]"
      style={{
        background: saving ? "hsl(187 72% 50% / 0.25)" : "hsl(187 72% 50%)",
        color: "hsl(var(--background))",
      }}
    >
      {saving ? t("agenda.saving") : t("agenda.saveChanges")}
    </button>
  );

  return (
    <AgendaBottomSheet
      open={open}
      onClose={onClose}
      title={t("agenda.editEvent")}
      footer={editFooter}
    >
      {/* Title */}
      <label className="block mb-4">
        <span className="text-[10px] font-display tracking-[0.16em] uppercase mb-1.5 block" style={labelStyle}>
          {t("agenda.eventTitle")}
        </span>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("agenda.eventTitlePlaceholder")}
          className="w-full rounded-xl px-4 py-3 text-[14px] font-display text-foreground placeholder:text-muted-foreground outline-none"
          style={inputStyle}
        />
      </label>

      {/* Tag */}
      <div className="mb-4">
        <span className="text-[10px] font-display tracking-[0.16em] uppercase mb-1.5 block" style={labelStyle}>
          {t("agenda.tag")}
        </span>
        <div className="flex gap-2">
          {TAG_OPTIONS.map((tOpt) => {
            const s = TAG_STYLES[tOpt] ?? TAG_STYLES.Personal;
            const active = tag === tOpt;
            return (
              <button
                key={tOpt}
                onClick={() => setTag(tOpt)}
                className="flex-1 py-2 rounded-xl text-[12px] font-display font-medium tracking-wide transition-all duration-200"
                style={{
                  background: active ? s.bg : "transparent",
                  border: `1px solid ${active ? s.text : "hsl(222 18% 20%)"}`,
                  color: active ? s.text : "hsl(218 14% 55%)",
                }}
              >
                {t(TAG_KEYS[tOpt] ?? TAG_KEYS.Personal)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Imported date-only schedules stay intact when editing other fields. */}
      {isAllDay(event) ? <div className="mb-4 space-y-2 text-sm text-white/80">
        <p>{t("ux.agenda.allDay")} · {formatAllDayDates(event, localeTagFor(lang))}</p>
        <p>{t("ux.agenda.editAllDayAtSource")}</p>
      </div> : <div className="flex flex-col gap-3 mb-4">
        <label className="block">
          <span className="text-[10px] font-display tracking-[0.16em] uppercase mb-1.5 block" style={labelStyle}>
            {t("agenda.start")}
          </span>
          <input
            type="datetime-local"
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
            className="w-full rounded-xl px-3 py-2.5 text-[13px] font-display text-foreground outline-none"
            style={{ ...inputStyle, colorScheme: "dark" }}
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-display tracking-[0.16em] uppercase mb-1.5 block" style={labelStyle}>
            {t("agenda.end")} <span style={{ color: "hsl(218 14% 45%)" }}>{t("agenda.optional")}</span>
          </span>
          <input
            type="datetime-local"
            value={endAt}
            onChange={(e) => setEndAt(e.target.value)}
            className="w-full rounded-xl px-3 py-2.5 text-[13px] font-display text-foreground outline-none"
            style={{ ...inputStyle, colorScheme: "dark" }}
          />
        </label>
      </div>}

      {/* Reminders — multi (cap 2). Persists to event_reminders on save. */}
      <RemindersEditor
        reminders={reminders}
        onChange={(next) => {
          setReminders(next);
          setReminderMinutes(next.length > 0 ? next[0] : null);
        }}
      />

      {/* Notes */}
      <label className="block mb-4">
        <span className="text-[10px] font-display tracking-[0.16em] uppercase mb-1.5 block" style={labelStyle}>
          {t("agenda.notes")} <span style={{ color: "hsl(218 14% 45%)" }}>{t("agenda.optional")}</span>
        </span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder={t("agenda.notesPlaceholder")}
          className="w-full rounded-xl px-4 py-3 text-[13px] font-display text-foreground placeholder:text-muted-foreground outline-none resize-none"
          style={inputStyle}
        />
      </label>

      {/* Delete zone */}
      <div className="mb-2">
        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            className="flex items-center gap-2 text-[11px] font-display tracking-wide transition-opacity hover:opacity-100 opacity-60"
            style={{ color: "hsl(0 70% 60%)" }}
          >
            <Trash2 size={12} />
            {t("agenda.deleteEvent")}
          </button>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl p-3"
            style={{ background: "hsl(0 70% 50% / 0.08)", border: "1px solid hsl(0 70% 50% / 0.20)" }}
          >
            <p className="text-[12px] font-display font-medium mb-1" style={{ color: "hsl(0 70% 65%)" }}>
              {t("agenda.deleteConfirm")}
            </p>
            <p className="text-[10px] font-display mb-3" style={{ color: "hsl(0 50% 55%)" }}>
              {t("agenda.deleteConfirmDesc")}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmDelete(false)}
                className="flex-1 py-2 rounded-xl text-[12px] font-display font-medium"
                style={{ border: "1px solid hsl(222 18% 20%)", color: "hsl(218 14% 65%)" }}
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2 rounded-xl text-[12px] font-display font-semibold"
                style={{ background: "hsl(0 70% 50%)", color: "#fff" }}
              >
                {deleting ? "…" : t("agenda.confirmDelete")}
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </AgendaBottomSheet>
  );
};

// ── Month grid helpers ─────────────────────────────────────────────────────

function getMonthGrid(viewMonth: Date): (Date | null)[][] {
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  // Monday = 0 offset
  const startOffset = (firstDay.getDay() + 6) % 7;

  const cells: (Date | null)[] = [];

  // Previous month padding
  for (let i = startOffset - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    cells.push(d);
  }

  // Current month
  for (let d = 1; d <= lastDay.getDate(); d++) {
    cells.push(new Date(year, month, d));
  }

  // Next month padding
  while (cells.length % 7 !== 0) {
    const d = new Date(year, month + 1, cells.length - startOffset - lastDay.getDate() + 1);
    cells.push(d);
  }

  // Split into weeks
  const weeks: (Date | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
}

function isSameMonth(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function formatMonthYear(d: Date, lang: Lang): string {
  const locale = localeTagFor(lang);
  const m = d.toLocaleDateString(locale, { month: "long" });
  return `${m.charAt(0).toUpperCase()}${m.slice(1)} ${d.getFullYear()}`;
}

// ── Particles & Glow Orbs ────────────────────────────────────────────────

const AGENDA_PARTICLES = Array.from({ length: 22 }, (_, i) => ({
  w: Math.random() * 2 + 1,
  color: i % 3 === 0 ? "#00e5ff" : i % 3 === 1 ? "hsl(265 60% 65%)" : "hsl(220 70% 65%)",
  opacity: Math.random() * 0.18 + 0.05,
  left: `${Math.random() * 100}%`,
  top: `${Math.random() * 100}%`,
  yDrift: -(Math.random() * 30 + 10),
  xDrift: Math.random() * 20 - 10,
  dur: Math.random() * 8 + 6,
  delay: Math.random() * 5,
}));

const AgendaParticles = React.memo(() => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
    {AGENDA_PARTICLES.map((p, i) => (
      <motion.div key={i} className="absolute rounded-full"
        style={{ width: p.w, height: p.w, background: p.color, opacity: p.opacity, left: p.left, top: p.top }}
        animate={{ y: [0, p.yDrift, 0], x: [0, p.xDrift, 0], opacity: [p.opacity, p.opacity + 0.14, p.opacity] }}
        transition={{ duration: p.dur, repeat: Infinity, ease: "easeInOut", delay: p.delay }} />
    ))}
  </div>
));
AgendaParticles.displayName = "AgendaParticles";

const AgendaGlowOrbs = React.memo(() => (
  <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
    <motion.div className="absolute w-[320px] h-[320px] rounded-full"
      style={{ top: "-5%", left: "-15%", background: "radial-gradient(circle, hsl(187 72% 30% / 0.22), transparent 70%)" }}
      animate={{ opacity: [0.3, 0.5, 0.3] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} />
    <motion.div className="absolute w-[260px] h-[260px] rounded-full"
      style={{ top: "40%", right: "-12%", background: "radial-gradient(circle, hsl(265 60% 30% / 0.18), transparent 70%)" }}
      animate={{ opacity: [0.2, 0.4, 0.2] }} transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 3 }} />
    <motion.div className="absolute w-[200px] h-[200px] rounded-full"
      style={{ bottom: "15%", left: "20%", background: "radial-gradient(circle, hsl(187 72% 50% / 0.10), transparent 70%)" }}
      animate={{ opacity: [0.15, 0.30, 0.15] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 5 }} />
  </div>
));
AgendaGlowOrbs.displayName = "AgendaGlowOrbs";

// ── Page ───────────────────────────────────────────────────────────────────

const SWIPE_THRESHOLD = 50;

const Agenda: React.FC<{ ownerId: string }> = ({ ownerId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, tf, lang } = useLanguage();
  const { addXP, totalXP, level, getLevelProgress } = useXP({ guarded: true });
  const today = useMemo(() => new Date(), []);
  const [selected, setSelected] = useState<Date>(today);
  const [viewMonth, setViewMonth] = useState<Date>(new Date(today.getFullYear(), today.getMonth(), 1));
  const monthGrid = useMemo(() => getMonthGrid(viewMonth), [viewMonth]);
  const visibleDays = monthGrid.flat().filter(Boolean) as Date[];
  const windowStart = startOfDay(visibleDays[0]);
  const windowEnd = startOfDay(visibleDays[visibleDays.length - 1]); windowEnd.setDate(windowEnd.getDate() + 1);
  const resource = useLocalAgendaResource({ ownerId, time_min: windowStart.toISOString(), time_max: windowEnd.toISOString() });
  const captureAgenda = resource.capture;
  const events = resource.events as AgendaEvent[];
  const loading = resource.loading, eventsError = resource.error;
  const reminderCounts = useMemo(() => Object.fromEntries(resource.events.map(event => [event.id, event.reminders.length])), [resource.events]);
  const fetchEvents = resource.refresh;
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<AgendaEvent | null>(null);
  const [slideDir, setSlideDir] = useState<1 | -1>(1);
  const [voicePrefill, setVoicePrefill] = useState<CreateEventPrefill | null>(null);

  // Email drafts arrive through a single-use capability bound to the original owner.
  useEffect(() => {
    const incoming = (location.state as { vyvAgendaContext?: unknown } | null)?.vyvAgendaContext;
    if (!incoming) return;
    let active = true;
    const current = captureAgenda(), assert = () => { if (!active || !current()) throw new Error("invalid_session"); };
    void (async () => {
      try {
        await agendaOwnerSession(ownerId, assert); assert();
        const draft = takeOwnedAgendaContext(incoming, ownerId); assert();
        if (draft) { setVoicePrefill({ title: draft.title, notes: draft.notes, tag: draft.tag, source: draft.source, expected_user_id: ownerId }); setModalOpen(true); }
        navigate(location.pathname + location.search, { replace: true, state: null });
      } catch { /* A changed identity or expired capability never exposes its draft. */ }
    })();
    return () => { active = false; };
  }, [location.key, location.pathname, location.search, location.state, navigate, ownerId, captureAgenda]);

  const currentUserId = ownerId;
  const tasksHook = useAgendaTasks(currentUserId);
  // Voice-first capture: record -> Whisper -> agenda-extract -> open modal
  // pre-filled with the most-confident extracted event. Multi-event in v1
  // is dropped server-side; the hook returns at most one event.
  const handleEventsExtracted = useCallback(
    (extracted: ExtractedAgendaEvent[], rawTranscript: string) => {
      if (!extracted.length) {
        toast.error(t("agenda.voice.noEventDetected"));
        return;
      }
      const e = extracted[0];
      setVoicePrefill({
        title: e.title,
        start_at: e.start_iso,
        end_at: e.end_iso,
        tag: e.tag,
        // Fold attendees into notes per v1 schema decision (no attendees col).
        notes: [
          e.notes,
          e.attendees.length ? `${t("agenda.event.attendees")}: ${e.attendees.join(", ")}` : null,
        ].filter(Boolean).join("\n") || null,
        location: e.location,
        reminder_minutes: e.reminder_minutes,
        transcript_raw: rawTranscript,
        confidence: e.confidence,
        source: "voice",
      });
      setModalOpen(true);
    },
    [t],
  );

  const voice = useAgendaVoiceCapture({
    lang,
    onEventsExtracted: handleEventsExtracted,
  });

  // A displayed task snapshot and reviewed event commit in one operation.
  const handlePromoteToEvent = useCallback((task: AgendaTask) => {
    const suggestedStart = task.due_date
      ? new Date(`${task.due_date.slice(0, 10)}T09:00:00`)
      : new Date(Date.now() + 60 * 60 * 1000);
    const suggestedEnd = new Date(suggestedStart.getTime() + 60 * 60 * 1000);
    const expected = snapshotForTask(task);
    if (!expected || expected.task.done || expected.task.external_id !== null) { toast.error(t("agendaAction.taskUnavailable")); return; }
    setVoicePrefill({
      title: task.title,
      start_at: suggestedStart.toISOString(),
      end_at: suggestedEnd.toISOString(),
      tag: "Personal",
      notes: null,
      source: "manual",
      task_id: task.id,
      expected_task_snapshot: structuredClone(expected),
      expected_user_id: ownerId,
    });
    setModalOpen(true);
  }, [ownerId, t]);

  // Calendar Premium Fase 1 — Mini-Mirror scorecard snapshot.
  const agendaSnapshot = useAgendaSnapshot();
  const refreshAgendaSnapshot = agendaSnapshot.refresh;

  // Listen for AgendaMM auto-create (PATH 1, 12/05/26) so the scorecard
  // hero ("next event") updates after the reviewed voice operation is confirmed.
  // CustomEvent is dispatched by AgendaMirrorProvider.
  useEffect(() => {
    const onRefresh = () => refreshAgendaSnapshot();
    window.addEventListener("vyv:agenda-refresh", onRefresh);
    return () => window.removeEventListener("vyv:agenda-refresh", onRefresh);
  }, [refreshAgendaSnapshot]);

  // ── Focus param (Notes → Agenda navigation, 12/05/26) ──
  // /agenda?focus=<event_id> jumps the calendar to the event's day and
  // briefly pulses its card. Strip the param after consumption so the
  // browser back button doesn't re-trigger on every nav.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusEventId = searchParams.get("focus");
  useEffect(() => {
    if (!focusEventId) return;
    const controller = new AbortController();
    let active = true;
    let frame: number | undefined;
    const target = events.find(event => event.id === focusEventId);
    if (target) {
      setSelected(eventStartDate(target));
      frame = window.requestAnimationFrame(() => {
        const node = document.getElementById(`agenda-event-${focusEventId}`);
        if (node) {
          node.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "center" });
          node.classList.add("vyv-agenda-focus-pulse");
          window.setTimeout(() => node.classList.remove("vyv-agenda-focus-pulse"), 1600);
        }
        const next = new URLSearchParams(searchParams); next.delete("focus");
        setSearchParams(next, { replace: true });
      });
    } else {
      // Deep links can point outside the currently loaded month (Mirror/Notes).
      void (async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!active || !user) return;
          const { data, error } = await supabase.from("agenda_events").select("*")
            .eq("user_id", user.id).eq("id", focusEventId).is("deleted_at", null)
            .abortSignal(controller.signal).maybeSingle();
          if (!active) return;
          if (error || !data) throw error || new Error("Event unavailable");
          const date = eventStartDate(data);
          setSelected(date); setViewMonth(new Date(date.getFullYear(), date.getMonth(), 1));
        } catch { if (active) toast.error(t("agenda.failedToLoad")); }
      })();
    }
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    return () => { active = false; controller.abort(); window.clearTimeout(timeout); if (frame !== undefined) window.cancelAnimationFrame(frame); };
  }, [focusEventId, events, searchParams, setSearchParams, t]);

  const handleVoiceTap = async () => {
    if (!voice.supported) {
      toast.error(t("agenda.voice.errorNoMic"));
      return;
    }
    if (voice.status === "recording") {
      await voice.stop();
    } else if (voice.status === "idle" || voice.status === "ready" || voice.status === "error") {
      await voice.start();
    }
  };

  const goMonth = (dir: 1 | -1) => {
    setSlideDir(dir);
    const next = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + dir, 1);
    setViewMonth(next);
    setSelected(new Date(next.getFullYear(), next.getMonth(), Math.min(selected.getDate(), new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate())));
  };

  useEffect(() => {
    const refresh = () => { void fetchEvents(); };
    window.addEventListener("vyv:agenda-refresh", refresh);
    return () => window.removeEventListener("vyv:agenda-refresh", refresh);
  }, [fetchEvents]);

  const selectedDayEvents = useMemo(
    () => events.filter((e) => occursDuringDay(e, selected)),
    [events, selected],
  );

  const eventDaysSet = useMemo(() => {
    const s = new Set<string>();
    for (const day of monthGrid.flat()) {
      if (day && events.some(event => eventOccursOn(event, day))) {
        s.add(`${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`);
      }
    }
    return s;
  }, [events, monthGrid]);

  const hasEventOn = (d: Date) => eventDaysSet.has(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);

  const calendarGesture = useRef<{ id: number; x: number; y: number } | null>(null);
  const calendarClickBlockedUntil = useRef(0);
  const finishCalendarGesture = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = calendarGesture.current; calendarGesture.current = null;
    if (!start || start.id !== event.pointerId) return;
    const dx = event.clientX - start.x, dy = event.clientY - start.y;
    if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.5) {
      calendarClickBlockedUntil.current = performance.now() + 300;
      goMonth(dx < 0 ? 1 : -1);
    }
  };

  const isToday = isSameDay(selected, today);

  return (
    <MotionConfig reducedMotion="user"><MobileLayout className="pb-28 pt-6 relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #0a0a0f 0%, #06090f 60%, #0a0a0f 100%)" }}>
      <style>{`
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
        @keyframes breatheGlow { 0%, 100% { opacity: 0.4; transform: scale(0.97); } 50% { opacity: 0.7; transform: scale(1.03); } }
        @keyframes rotatingBorder { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }
        @keyframes vyv-agenda-focus-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(34,211,238,0.55), 0 0 24px 0 rgba(34,211,238,0.35); }
          70%  { box-shadow: 0 0 0 8px rgba(34,211,238,0.00), 0 0 28px 8px rgba(34,211,238,0.18); }
          100% { box-shadow: 0 0 0 0 rgba(34,211,238,0.00), 0 0 0 0 rgba(34,211,238,0.00); }
        }
        .vyv-agenda-focus-pulse { animation: vyv-agenda-focus-pulse 1.6s ease-out; border-radius: 16px; }
      `}</style>


      {/* BACK BUTTON + NOTES SHORTCUT */}
      <div className="flex items-center justify-between mb-3 relative z-10">
        <MirrorBackButton />
        <button
          onClick={() => navigate("/notes")}
          className="flex items-center gap-1.5 text-cyan-200 hover:text-white active:opacity-80 transition-all duration-200 hover:-translate-y-px px-2.5 py-1.5 rounded-lg"
          style={{
            background: "hsl(187 72% 50% / 0.08)",
            border: "1px solid hsl(187 72% 50% / 0.45)",
            boxShadow: "0 0 14px hsl(187 72% 50% / 0.28), inset 0 0 8px hsl(187 72% 50% / 0.06)",
          }}
          aria-label={t("agenda.notes")}
        >
          <StickyNote size={14} />
          <span className="text-[12px] font-display">{t("agenda.notes")}</span>
        </button>
      </div>

      {/* ── SECTION 1: AGENDA MINI-MIRROR (Calendar Premium Fase 1) ──
          Card hero starts directly with avatar + scorecard split.
          Title removed — user already contextualized via Mirror → Calendar nav.
          Replicate this clean-card pattern in Fitness + Nutrition Mini-Mirrors. */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.5 }}
        className="rounded-2xl p-5 mb-5 relative z-10 ring-1 ring-white/5"
        style={{ background: "linear-gradient(135deg, #0a0e1a 0%, #0f1419 50%, #0a0e1a 100%)" }}
      >
        <AgendaMirrorProvider>
          {/* Row 1 — avatar (left) + scorecard (right) */}
          <div className="flex flex-wrap items-stretch justify-center gap-5">
            <AgendaMiniMirrorAvatarSlot />
            <AgendaMiniMirrorScorecard
              todayCount={agendaSnapshot.todayCount}
              next7dCount={agendaSnapshot.next7dCount}
              openTasksCount={agendaSnapshot.openTasksCount}
              nextEvent={agendaSnapshot.nextEvent}
              loading={agendaSnapshot.loading}
              error={agendaSnapshot.error}
              onRefresh={agendaSnapshot.refresh}
              tasks={agendaSnapshot.tasks}
              onToggleTask={agendaSnapshot.toggleTask}
              onUpdateTask={agendaSnapshot.updateTask}
              onDeleteTask={agendaSnapshot.deleteTask}
            />
          </div>
          {/* Row 2 — mic + response, full card width */}
          <div className="mt-5 pt-4 border-t border-white/5">
            <AgendaMiniMirrorVoiceSlot />
          </div>
        </AgendaMirrorProvider>
      </motion.div>

      {/* Voice-first capture button (Mic hero) — REMOVED Calendar Fase 1.
          Replaced by AgendaMiniMirror above. Voice-driven event creation
          now flows through the Mirror conversation (tool propose_agenda_event)
          OR through the + button in the month nav (CreateEventModal).
          useAgendaVoiceCapture hook file is retained for now; cleanup in a
          later phase per the Fase 1 decision contract. */}

      {/* XP Bar */}
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.4 }}
        className="flex items-center gap-3 rounded-xl px-3 py-2.5 mb-4 relative z-10"
        style={{ background: "linear-gradient(135deg, hsl(187 60% 10%), hsl(220 35% 8%))", border: "1px solid hsl(187 72% 50% / 0.22)" }}>
        <span className="px-2.5 py-1 rounded-full font-display text-[10px] font-bold shrink-0"
          style={{ background: "hsl(187 72% 50% / 0.15)", border: "1px solid hsl(187 72% 50% / 0.35)", color: "hsl(187 72% 65%)", textShadow: "0 0 8px hsl(187 72% 50% / 0.40)" }}>
          {tf("habits.xp.level", { level: String(level) })}
        </span>
        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "hsl(218 14% 18%)" }}>
          <motion.div className="h-full rounded-full" initial={{ width: 0 }} animate={{ width: `${getLevelProgress()}%` }}
            transition={{ delay: 0.5, duration: 0.9, ease: "easeOut" }}
            style={{ background: "linear-gradient(90deg, hsl(187 60% 40%), hsl(187 72% 60%))", boxShadow: "0 0 8px hsl(187 72% 50% / 0.45)" }} />
        </div>
        <span className="text-[9px] font-display shrink-0" style={{ color: "hsl(218 14% 55%)" }}>{totalXP} XP</span>
        <motion.div animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.2, 1] }} transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}>
          <Sparkles size={12} style={{ color: "hsl(187 72% 60%)" }} />
        </motion.div>
      </motion.div>

      {/* ── SECTION 2: MONTH NAV + GRID (Bug B — integrated, no gap) ── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08, duration: 0.5 }}
        className="rounded-2xl p-3 mb-5 overflow-hidden relative z-10"
        style={{ background: "linear-gradient(145deg, hsl(220 40% 8%), hsl(220 35% 5%))", border: "1px solid hsl(187 72% 50% / 0.22)", boxShadow: "0 0 40px hsl(187 72% 50% / 0.08), inset 0 0 60px hsl(187 72% 50% / 0.03)" }}
      >
        {/* Star field */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
          {Array.from({ length: 12 }, (_, si) => (
            <motion.div key={si} className="absolute rounded-full"
              style={{ width: si % 4 === 0 ? 1.5 : 0.8, height: si % 4 === 0 ? 1.5 : 0.8, background: si % 3 === 0 ? "#00e5ff" : si % 3 === 1 ? "hsl(265 60% 75%)" : "white", opacity: 0.10, left: `${(si * 8.7) % 95}%`, top: `${(si * 12.3) % 95}%` }}
              animate={{ opacity: [0.04, 0.16, 0.04] }}
              transition={{ duration: 4 + si * 0.5, repeat: Infinity, ease: "easeInOut", delay: si * 0.4 }} />
          ))}
        </div>

        {/* Month nav — integrated into grid card header (Bug B) */}
        <div className="flex items-center justify-between px-1 mb-3 relative z-10">
          <button
            onClick={() => goMonth(-1)}
            className="w-9 h-9 rounded-xl flex items-center justify-center active:scale-90 transition-all duration-200 hover:shadow-[0_0_12px_hsl(187_72%_50%_/_0.3)]"
            style={{ background: "hsl(187 72% 50% / 0.10)", border: "1px solid hsl(187 72% 50% / 0.20)" }}
            aria-label="prev month"
          >
            <ChevronLeft size={16} style={{ color: "hsl(var(--primary))" }} />
          </button>
          <p className="font-display text-[18px] font-bold text-white">
            {formatMonthYear(viewMonth, lang)}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => goMonth(1)}
              className="w-9 h-9 rounded-xl flex items-center justify-center active:scale-90 transition-all duration-200 hover:shadow-[0_0_12px_hsl(187_72%_50%_/_0.3)]"
              style={{ background: "hsl(187 72% 50% / 0.10)", border: "1px solid hsl(187 72% 50% / 0.20)" }}
              aria-label="next month"
            >
              <ChevronRight size={16} style={{ color: "hsl(var(--primary))" }} />
            </button>
            <button
              onClick={() => {
                setVoicePrefill(null);
                setModalOpen(true);
              }}
              className="w-9 h-9 rounded-xl flex items-center justify-center active:scale-90 transition-all duration-200 hover:shadow-[0_0_12px_hsl(187_72%_50%_/_0.3)]"
              style={{ background: "hsl(187 72% 50% / 0.12)", border: "1px solid hsl(187 72% 50% / 0.25)" }}
              aria-label={t("agenda.newEvent")}
            >
              <Plus size={16} style={{ color: "hsl(var(--primary))" }} />
            </button>
          </div>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 mb-2">
          {DAY_KEYS.map((key) => (
            <div key={key} className="text-center">
              <span
                className="text-[9px] font-display tracking-[0.2em] uppercase"
                style={{ color: "hsl(187 72% 50% / 0.45)" }}
              >
                {t(key)}
              </span>
            </div>
          ))}
        </div>

        {/* A day tap never moves the grid; only a deliberate swipe changes month. */}
        <div className="min-w-0 overflow-hidden">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`${viewMonth.getFullYear()}-${viewMonth.getMonth()}`}
            initial={{ x: slideDir * 80, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: slideDir * -80, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            data-testid="agenda-month-grid"
            onPointerDown={event => { if (event.isPrimary && event.button === 0) calendarGesture.current = { id: event.pointerId, x: event.clientX, y: event.clientY }; }}
            onPointerUp={finishCalendarGesture}
            onPointerCancel={() => { calendarGesture.current = null; }}
            className="touch-pan-y min-w-0"
          >
            {monthGrid.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7">
                {week.map((day, di) => {
                  if (!day) return <div key={di} />;
                  const inMonth = isSameMonth(day, viewMonth);
                  const isSelected = isSameDay(day, selected);
                  const isTodayCell = isSameDay(day, today);
                  const hasEvt = hasEventOn(day);

                  return (
                    <button
                      key={di}
                      type="button"
                      aria-pressed={isSelected}
                      aria-label={day.toLocaleDateString(localeTagFor(lang), { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                      onClick={() => {
                        if (performance.now() < calendarClickBlockedUntil.current) return;
                        setSelected(new Date(day));
                        if (!isSameMonth(day, viewMonth)) {
                          setSlideDir(day < viewMonth ? -1 : 1);
                          setViewMonth(new Date(day.getFullYear(), day.getMonth(), 1));
                        }
                      }}
                      className="flex min-w-0 w-full flex-col items-center justify-center min-h-[44px] rounded-xl transition-colors duration-150"
                      style={
                        isTodayCell && isSelected
                          ? { background: "hsl(var(--primary))", borderRadius: 12, boxShadow: "0 0 16px hsl(187 72% 50% / 0.5)" }
                          : isTodayCell
                          ? { background: "hsl(var(--primary))", borderRadius: 12, boxShadow: "0 0 16px hsl(187 72% 50% / 0.5)" }
                          : isSelected
                          ? { background: "hsl(187 72% 50% / 0.20)", border: "1px solid hsl(187 72% 50% / 0.50)", borderRadius: 12 }
                          : undefined
                      }
                    >
                      <span
                        className="text-[13px] font-display font-semibold leading-none"
                        style={{
                          color: isTodayCell
                            ? "#06090f"
                            : isSelected
                            ? "#00e5ff"
                            : !inMonth
                            ? "hsl(218 14% 20%)"
                            : "hsl(210 20% 78%)",
                        }}
                      >
                        {day.getDate()}
                      </span>
                      {hasEvt && (
                        <div
                          className="w-1.5 h-1.5 rounded-full mt-0.5"
                          style={{ background: isTodayCell ? "#06090f" : "#00e5ff", filter: isTodayCell ? "none" : "drop-shadow(0 0 3px #00e5ff)" }}
                        />
                      )}
                      {!hasEvt && <div className="w-1.5 h-1.5 mt-0.5" />}
                    </button>
                  );
                })}
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
        </div>
      </motion.div>

      {/* ── SECTION 2.5: TASKS CARD (Frente C Fase 1, 12/05/26) ──
          Mounts between the month calendar and the HOJE event list.
          Shares the parent's weekly task state; owns its quick-add sheet
          and inline edit. Promotion to event delegates upward via
          handlePromoteToEvent which opens CreateEventModal prefilled with
          the displayed task snapshot for the atomic reviewed promotion. */}
      {currentUserId && (
        <AgendaTasksCard
          userId={currentUserId}
          tasksState={tasksHook}
          onCreated={() => { /* Confirmed task changes update both hooks. */ }}
          onPromoteToEvent={handlePromoteToEvent}
          onVoiceEvent={handleEventsExtracted}
        />
      )}

      {/* ── SECTION 3: EVENT LIST ── */}
      {resource.status === "ready" && !resource.complete && <p role="status" className="mb-3 text-sm text-muted-foreground">{t("agendaAction.partial")}</p>}
      {eventsError && <section className="mb-3 space-y-2"><p role="alert">{t("agenda.failedToLoad")}</p><button className="min-h-11 rounded-xl border px-4" onClick={() => void fetchEvents()}>{t("common.retry")}</button></section>}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Clock size={20} className="animate-spin" style={{ color: "hsl(187 72% 50% / 0.4)" }} />
        </div>
      ) : eventsError ? null : selectedDayEvents.length > 0 ? (
        <>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.14 }}
            className="text-[11px] font-display tracking-[0.18em] uppercase mb-3"
            style={{ color: "hsl(187 72% 50% / 0.55)" }}
          >
            {isToday ? t("agenda.today") : selected.toLocaleDateString(localeTagFor(lang), { weekday: "long", month: "short", day: "numeric" })}
          </motion.p>
          {selectedDayEvents.map((ev, i) => (
            <motion.div
              key={ev.id}
              id={`agenda-event-${ev.id}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.14 + i * 0.07 }}
            >
              <EventCard event={ev} reminderCount={reminderCounts[ev.id]} onTap={setEditingEvent} />
            </motion.div>
          ))}
        </>
      ) : !resource.complete ? null : (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.14 }}
          className="flex flex-col items-center justify-center text-center py-12 px-6 rounded-2xl relative overflow-hidden"
          style={{ background: "linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.75)), url('https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/mirror-brief/room%20ex_013.jpeg') center/cover no-repeat", border: "1px solid hsl(187 72% 50% / 0.18)" }}
        >
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
            style={{ background: "hsl(187 72% 50% / 0.08)", border: "1px solid hsl(187 72% 50% / 0.18)" }}
          >
            <CalendarDays size={22} style={{ color: "hsl(187 72% 50% / 0.60)" }} />
          </div>
          <p className="font-display text-[14px] font-medium text-foreground/60 leading-snug mb-5">
            {t("agenda.dayOpen")}
            <br />
            {t("agenda.plan.emptyHint")}
          </p>
          <button
            onClick={() => { setVoicePrefill(null); setModalOpen(true); }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-display text-[12px] font-medium tracking-wide transition-all duration-200 active:scale-95"
            style={{ background: "hsl(187 72% 50% / 0.12)", border: "1px solid hsl(187 72% 50% / 0.25)", color: "hsl(187 72% 55%)" }}
          >
            {t("agenda.newEvent")}
            <ChevronLeft size={13} className="rotate-180" />
          </button>
        </motion.div>
      )}

      {/* "Módulos Conectados" grid removed 19/05 — duplicates the bottom
          nav, breaks the premium minimalism, and the Mini Mirror at the
          top of /agenda already covers the "Mirror presence" role this
          grid was trying to fill. See feat(agenda) cleanup PR. */}

      <AppleCalendarPendingActionLauncher ownerId={ownerId} onUpdated={fetchEvents}/>
      <AgendaPendingActionLauncher ownerId={ownerId} onMirrorResume={locator => navigate("/mirror", { state: { agendaActionRecovery: locator } })} />

      {/* CREATE MODAL */}
      <CreateEventModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          // Drop the voice prefill so the next manual "+" tap opens fresh.
          setVoicePrefill(null);
          voice.reset();
        }}
        onCreated={() => { void fetchEvents(); void tasksHook.refetch(); }}
        defaultDate={selected}
        prefill={voicePrefill}
        onXP={async (userId, eventId, isCurrent) => {
          if (!isCurrent()) return;
          const { leveledUp, newLevel, inserted } = await addXP("agenda", "event_created", 12, eventId, { ownerId: userId, isCurrent });
          if (!inserted || !isCurrent()) return;
          toast.custom(() => (
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl"
              style={{ background: "hsl(220 35% 12%)", border: "1px solid hsl(187 72% 50% / 0.35)" }}>
              <CalendarDays size={13} style={{ color: "hsl(187 72% 55%)" }} />
              <span className="font-display text-[13px] font-semibold" style={{ color: "hsl(187 72% 60%)" }}>
                +12 XP — {t("agenda.eventCreated")}
              </span>
            </div>
          ), { duration: 2000 });
          if (leveledUp) {
            setTimeout(() => {
              if (!isCurrent()) return;
              toast.custom(() => (
                <div className="flex items-center gap-2 px-5 py-3 rounded-xl"
                  style={{ background: "linear-gradient(135deg, hsl(187 72% 20%), hsl(265 60% 15%))", border: "1px solid hsl(187 72% 50% / 0.50)" }}>
                  <span className="text-[20px]">{"\u{2B50}"}</span>
                  <span className="font-display text-[15px] font-bold text-white">
                    {t("habits.xp.levelUp")} {newLevel}
                  </span>
                </div>
              ), { duration: 3500 });
            }, 600);
          }
        }}
      />

      {/* EDIT MODAL */}
      <EditEventModal
        event={editingEvent}
        onClose={() => setEditingEvent(null)}
        onUpdated={fetchEvents}
      />

    </MobileLayout></MotionConfig>
  );
};

function BoundAgendaPage() {
  const identity = useLibraryIdentity();
  return identity.ready && identity.userId ? <Agenda key={identity.userId + ":" + identity.generation} ownerId={identity.userId} /> : null;
}
export default function AgendaPage() {
  return <AgendaSessionBoundary><BoundAgendaPage /></AgendaSessionBoundary>;
}
